import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tag } from 'primereact/tag';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Timeline } from 'primereact/timeline';
import { Dialog } from 'primereact/dialog';
import { TabView, TabPanel } from 'primereact/tabview';
import QuestionImage from '../../../components/QuestionImage';
import { AttemptResultPieChart } from '../components/AttemptResultPieChart';
import { ModuleBreakdownBarChart, buildModuleBreakdown } from '../components/ModuleBreakdownBarChart';
import { QuestionTimeChart } from '../components/QuestionTimeChart';
import { DashboardService } from '../../../service/DashboardService';

const dashboardService = new DashboardService();
const PAGE_SIZE = 3;
const SCROLL_THRESHOLD_PX = 200;

const scoreSeverity = (score) => {
    if (score == null) return null;
    if (score >= 70) return 'success';
    if (score >= 50) return 'warning';
    return 'danger';
};

// Mesmo esquema de cor do severity acima, mas em hex — o marcador do
// Timeline usa backgroundColor direto, não aceita as classes do Tag.
const scoreColor = (score) => {
    if (score == null) return '#9E9E9E';
    if (score >= 70) return '#22C55E';
    if (score >= 50) return '#F59E0B';
    return '#EF4444';
};

const itemTitle = (item) => {
    if (item.type === 'practiceTest') return 'Simulado';
    return item.moduleName || (item.testTitle
        ? `${item.testTitle}${item.testYear ? ` (${item.testYear})` : ''}`
        : 'Quiz');
};

// Mesma distinção usada dentro do app (quiz_history_page.dart) entre os dois
// fluxos de quiz objetivo — por módulo ou por prova anterior sorteada.
const quizTypeLabel = (quizType) => {
    if (quizType === 'questionsByModule') return 'Questões Objetivas (por módulo)';
    if (quizType === 'pastTestQuestions') return 'Questões Objetivas (provas anteriores)';
    return 'Quiz';
};

const formatDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString('pt-BR');
};

// time vem em segundos inteiros (Answer.time / PracticeTestAnswer.time).
const formatDuration = (totalSeconds) => {
    if (totalSeconds == null) return null;
    const seconds = Math.round(totalSeconds);
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}h ${String(m).padStart(2, '0')}min`;
    if (m > 0) return `${m}min ${String(s).padStart(2, '0')}s`;
    return `${s}s`;
};

const studentDisplayName = (student) => {
    if (!student) return '';
    const name = [student.firstName, student.lastName].filter(Boolean).join(' ');
    return name || student.email;
};

// Detalhamento individualizado do aluno: página própria (não modal), pra
// poder ser acessada/recarregada direto pela URL — /students/:id/timeline.
// Histórico de tentativas (Quiz e Simulado) mais granular que a linha
// agregada do ranking, carregado com lazy loading + infinite scroll de
// verdade (não paginação por números): busca só a primeira página (3 itens)
// no load inicial, e acompanha o scroll da PÁGINA inteira (não uma caixa
// interna com altura fixa) — ao chegar perto do fim do documento, busca a
// próxima página de 3 e concatena ao que já foi carregado.
export const StudentTimelinePage = () => {
    const { id } = useParams();
    const history = useHistory();
    const location = useLocation();
    // A linha da grid de Ranking já tem nome/serviço/UF — chega pelo state
    // da navegação (history.push(path, { student: rowData })), usado como
    // valor inicial sem esperar a primeira página do timeline responder.
    const [student, setStudent] = useState(location.state?.student ?? null);
    const [items, setItems] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);

    const [attemptVisible, setAttemptVisible] = useState(false);
    const [attemptContext, setAttemptContext] = useState(null);
    const [attemptItems, setAttemptItems] = useState([]);
    const [attemptLoading, setAttemptLoading] = useState(false);
    const [attemptError, setAttemptError] = useState(null);

    const fetchPage = useCallback(async (offset, append) => {
        append ? setLoadingMore(true) : setLoading(true);
        setError(null);
        try {
            const page = await dashboardService.getStudentTimeline(id, { offset, limit: PAGE_SIZE });
            setStudent(page.student || null);
            setItems(prev => append ? [...prev, ...(page.data || [])] : (page.data || []));
            setTotalRecords(page.totalRecords || 0);
        } catch (e) {
            setError('Não foi possível carregar o histórico do aluno.');
        } finally {
            append ? setLoadingMore(false) : setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        setItems([]);
        setTotalRecords(0);
        setStudent(location.state?.student ?? null);
        fetchPage(0, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // Com PAGE_SIZE=3, a primeira leva pode não preencher a altura da tela —
    // nesse caso ainda NÃO existe scroll possível, então nenhum evento de
    // scroll dispararia o carregamento seguinte (o usuário nunca teria como
    // "chegar perto do fim" de uma página que nem é rolável ainda). Esse
    // efeito garante que a página cresça até ficar realmente rolável (ou até
    // acabarem os registros) — a partir daí, o carregamento das próximas
    // páginas passa a depender só do scroll de verdade do usuário (efeito
    // abaixo). Sem isso, um aluno com poucos registros carregaria tudo de
    // uma vez (não é bug, é esperado — não tem scroll real pra fatiar), mas
    // um aluno com centenas de tentativas dependeria só disso pra sequer
    // começar a rolar.
    useEffect(() => {
        if (loading || loadingMore) return;
        if (items.length === 0 || items.length >= totalRecords) return;
        if (document.documentElement.scrollHeight <= window.innerHeight) {
            fetchPage(items.length, true);
        }
    }, [items, totalRecords, loading, loadingMore, fetchPage]);

    // Infinite scroll de verdade: acompanha o scroll da PÁGINA (não um box
    // interno com altura fixa) — ao chegar perto do fim do documento, busca
    // a próxima página (3 itens por vez) e concatena ao que já foi carregado.
    useEffect(() => {
        const handleWindowScroll = () => {
            if (loading || loadingMore) return;
            if (items.length >= totalRecords) return;
            const scrollTop = window.scrollY || document.documentElement.scrollTop;
            const distanceToBottom = document.documentElement.scrollHeight - (scrollTop + window.innerHeight);
            if (distanceToBottom < SCROLL_THRESHOLD_PX) {
                fetchPage(items.length, true);
            }
        };
        window.addEventListener('scroll', handleWindowScroll);
        return () => window.removeEventListener('scroll', handleWindowScroll);
    }, [loading, loadingMore, items.length, totalRecords, fetchPage]);

    // Gabarito questão a questão dessa tentativa específica — Quiz busca
    // pelo contexto módulo/prova (sempre a tentativa mais atual, ver
    // comentário do backend); Simulado busca pelo practiceTestUuid, que
    // identifica a tentativa de forma exclusiva.
    const openAttemptDetail = async (item) => {
        setAttemptVisible(true);
        setAttemptContext(item);
        setAttemptItems([]);
        setAttemptError(null);
        setAttemptLoading(true);
        try {
            const details = item.type === 'quiz'
                ? await dashboardService.getQuizAttemptDetail(id, { moduleId: item.moduleId, testId: item.testId })
                : await dashboardService.getPracticeTestAttemptDetail(id, item.practiceTestUuid);
            setAttemptItems(details || []);
        } catch (e) {
            setAttemptError('Não foi possível carregar o detalhamento desta tentativa.');
        } finally {
            setAttemptLoading(false);
        }
    };

    const moduleBreakdown = useMemo(() => buildModuleBreakdown(attemptItems), [attemptItems]);

    const timelineMarker = (item) => (
        <span
            className="flex w-2rem h-2rem align-items-center justify-content-center text-white border-circle z-1 shadow-2"
            style={{ backgroundColor: scoreColor(item.score) }}
        >
            <i className={item.type === 'quiz' ? 'pi pi-book' : 'pi pi-clipboard'} />
        </span>
    );

    const timelineContent = (item) => (
        <Card
            title={itemTitle(item)}
            subTitle={item.type === 'quiz' ? quizTypeLabel(item.quizType) : 'Simulado'}
            className="mb-0 cursor-pointer hover:shadow-4 transition-shadow transition-duration-150"
            onClick={() => openAttemptDetail(item)}
        >
            <div className="flex justify-content-between align-items-center gap-2 mb-2">
                <span className="text-sm text-color-secondary">{formatDate(item.completedAt)}</span>
                <Tag value={`${item.score}%`} severity={scoreSeverity(item.score)} />
            </div>
            <p className="m-0 text-sm">
                {item.correctAnswers} certas / {item.wrongAnswers} erradas / {item.notAnswered} em branco
                {' '}(de {item.totalQuestions})
                {item.totalTimeSeconds != null && ` — ${formatDuration(item.totalTimeSeconds)}`}
            </p>
        </Card>
    );

    return (
        <div className="grid p-fluid timeline-demo">
            <div className="col-12">
                <div className="card">
                    <div className="flex align-items-center justify-content-between gap-3 mb-4">
                        <div className="flex align-items-center gap-3">
                            <Button
                                icon="pi pi-arrow-left"
                                rounded
                                text
                                // goBack() (POP) em vez de push('/students-ranking') — é
                                // o que deixa a tabela de ranking (StudentsRankingTable)
                                // saber que isso foi um "voltar" e não uma navegação
                                // nova pelo menu, pra decidir se reaproveita o estado ou
                                // reseta (mesmo padrão de UsersView.js/UserDetailsPage.js).
                                onClick={() => history.goBack()}
                            />
                            <div>
                                <h5 className="m-0">{studentDisplayName(student)}</h5>
                                {student && (
                                    <span className="text-color-secondary text-sm">
                                        {[student.serviceName, student.uf].filter(Boolean).join(' — ') || student.email}
                                    </span>
                                )}
                            </div>
                        </div>
                        <Button
                            icon="pi pi-refresh"
                            rounded
                            text
                            loading={loading}
                            onClick={() => {
                                setItems([]);
                                setTotalRecords(0);
                                fetchPage(0, false);
                            }}
                        />
                    </div>

                    {loading ? (
                        <div className="flex justify-content-center p-5">
                            <ProgressSpinner />
                        </div>
                    ) : error ? (
                        <p>{error}</p>
                    ) : items.length === 0 ? (
                        <p>Nenhuma tentativa de quiz ou simulado registrada.</p>
                    ) : (
                        <>
                            <Timeline
                                value={items}
                                align="alternate"
                                className="customized-timeline"
                                marker={timelineMarker}
                                content={timelineContent}
                            />
                            {loadingMore && (
                                <div className="flex justify-content-center p-3">
                                    <ProgressSpinner style={{ width: '2rem', height: '2rem' }} strokeWidth="4" />
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            <Dialog
                header={attemptContext ? `Gabarito — ${itemTitle(attemptContext)}` : 'Gabarito'}
                visible={attemptVisible}
                onHide={() => setAttemptVisible(false)}
                style={{ width: '45rem', maxWidth: '95vw' }}
            >
                {attemptContext && (
                    <div className="flex justify-content-between align-items-center gap-2 mb-3 pb-3" style={{ borderBottom: '1px solid var(--surface-300)' }}>
                        <span className="text-sm text-color-secondary">{formatDate(attemptContext.completedAt)}</span>
                        <Tag value={`${attemptContext.score}%`} severity={scoreSeverity(attemptContext.score)} />
                    </div>
                )}
                {attemptContext && (
                    <p className="text-sm mt-0 mb-3">
                        {attemptContext.correctAnswers} certas / {attemptContext.wrongAnswers} erradas / {attemptContext.notAnswered} em branco
                        {' '}(de {attemptContext.totalQuestions})
                        {attemptContext.totalTimeSeconds != null && ` — ${formatDuration(attemptContext.totalTimeSeconds)}`}
                    </p>
                )}
                {attemptContext && (
                    <TabView className="mb-3">
                        <TabPanel header="Resumo">
                            <AttemptResultPieChart
                                correct={attemptContext.correctAnswers}
                                wrong={attemptContext.wrongAnswers}
                                blank={attemptContext.notAnswered}
                            />
                        </TabPanel>
                        {moduleBreakdown.length >= 2 && (
                            <TabPanel header="Por módulo">
                                <ModuleBreakdownBarChart data={moduleBreakdown} />
                            </TabPanel>
                        )}
                        {attemptContext.type === 'practiceTest' && attemptItems.length > 0 && (
                            <TabPanel header="Tempo por questão">
                                <QuestionTimeChart attemptItems={attemptItems} />
                            </TabPanel>
                        )}
                    </TabView>
                )}
                {attemptLoading ? (
                    <div className="flex justify-content-center p-5">
                        <ProgressSpinner />
                    </div>
                ) : attemptError ? (
                    <p>{attemptError}</p>
                ) : attemptItems.length === 0 ? (
                    <p>Nenhuma questão respondida encontrada pra essa tentativa.</p>
                ) : (
                    <div>
                        {attemptItems.map((q, index) => (
                            <div
                                key={q.questionId}
                                className="mb-3 p-2 border-round"
                                style={
                                    q.selectedChoiceId == null
                                        ? { border: '1px solid var(--surface-300)', background: 'var(--surface-50)' }
                                        // Cor fixa (não var(--green-*)/var(--red-*), que no dark
                                        // theme mantêm o mesmo tom claro do light theme mas o texto
                                        // herda a cor clara do tema, ficando ilegível) — mesmo par
                                        // fundo/texto dos badges "Ativo"/"Inativo" (product-badge
                                        // status-instock/outofstock), que funciona em qualquer tema.
                                        : q.isCorrect
                                            ? { border: '2px solid #256029', background: '#C8E6C9', color: '#256029' }
                                            : { border: '1px solid #C63737', background: '#FFCDD2', color: '#C63737' }
                                }
                            >
                                <div className="flex align-items-start gap-2 mb-2">
                                    {/* q.order é a posição real que o aluno viu (só existe pra
                                        simulado, sincronizada com PracticeTestAnswer); tentativas
                                        sem esse dado (sincronizadas antes do campo existir, ou
                                        gabarito de quiz, que não tem esse conceito) caem pra
                                        ordem de chegada da API, como antes. */}
                                    <strong>{(q.order ?? index + 1)}.</strong>
                                    <div className="flex-1">
                                        <div className="text-center mb-2">
                                            <QuestionImage questionId={q.questionId} type="main" width={160} />
                                        </div>
                                        {q.questionTitle && (
                                            <p className="m-0 mb-1"><strong>{q.questionTitle}</strong></p>
                                        )}
                                        <p className="m-0" style={{ whiteSpace: 'pre-wrap' }}>
                                            {q.description}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex align-items-center gap-2">
                                    <i className={
                                        q.selectedChoiceId == null
                                            ? 'pi pi-minus-circle text-color-secondary'
                                            : (q.isCorrect ? 'pi pi-check-circle text-green-600' : 'pi pi-times-circle text-red-500')
                                    } />
                                    <span className="text-sm">
                                        Resposta do aluno: {q.selectedChoiceDescription || 'Não respondida'}
                                        {q.timeSeconds != null && ` (${formatDuration(q.timeSeconds)})`}
                                    </span>
                                </div>
                                {!q.isCorrect && (
                                    // Sem text-color-secondary (força cor via !important, ignora
                                    // o color fixo do pai) — herda o mesmo tom escuro do box, em
                                    // vez do cinza claro do tema (ilegível sobre o fundo vermelho).
                                    <div className="text-sm mt-1">
                                        Resposta correta: {q.correctChoiceDescription || '—'}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </Dialog>
        </div>
    );
};

export default StudentTimelinePage;
